import React, { ReactElement, useState } from 'react';
import classnames from 'classnames';
import { FieldError, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Icon,
  IconButton,
  SelectOption,
} from '../../../../../../../components/CiscoUi';
import {
  ControlledCheckbox,
  ControlledSelect,
  ControlledTextfield,
} from '../../../../../../../components';
import { ControlledMultipleSelect } from '../../../../../../../components/Form/ControlledMultipleSelect';
import { Tooltip } from '../../../../../../../components/Tooltip';
import {
  getTrunkedNetworkOptions,
  getTrunkedNetworkUids,
  resolveTrunkedNetworksSelection,
} from '../Utils';
import {
  determineIpErrorToDisplay,
  determineMacErrorToDisplay,
  determineNetworkErrorToDisplay,
  determineShowDeleteIcon,
  handleTrunkedChange,
  shouldExpandFormContainerForDropdown,
} from './utils';
import { useConfigureInterfaceValidation } from './hooks/use-configure-interface-validation';
import styles from './ConfigureFormFields.module.css';

export interface ConfigureFormFieldsProps {
  interfaces: NetworkToConfigure[];
  options: SelectOption<string>[];
  networks: Network[];
}

export const ConfigureFormFields = ({
  networks,
  options,
  interfaces,
}: ConfigureFormFieldsProps): ReactElement => {
  const [hiddenRows, setHiddenRows] = useState<number[]>([]);
  const [openDropdownRowIndex, setOpenDropdownRowIndex] = useState<
    number | undefined
  >();
  const { t } = useTranslation();
  const { control, errors, trigger, watch, getValues, setValue } =
    useFormContext<AddVmConfigureFormValues>();
  const { fields } = useFieldArray({
    control,
    name: 'interfaces',
  });

  useConfigureInterfaceValidation({
    getValues,
    interfaces,
    trigger,
    watch,
  });

  const determineShouldShowAccessFields = (index: number) => {
    const values = getValues();
    const row = values.interfaces?.[index];
    return !row?.trunked && row?.network;
  };

  const determineShouldHideRow = (index: number) => {
    return !hiddenRows.includes(index);
  };

  const onChange = handleTrunkedChange(interfaces, setValue, trigger);

  const handleTrunkedNetworksChange = (index: number) => (next: string[]) =>
    resolveTrunkedNetworksSelection(
      getValues().interfaces?.[index]?.trunkedNetworks ?? [],
      next,
      getTrunkedNetworkUids(networks),
    );

  const watchedInterfaces = watch('interfaces');

  const shouldExpandForDropdown = shouldExpandFormContainerForDropdown({
    hiddenRows,
    openDropdownRowIndex,
    rowCount: fields.length,
  });

  return (
    <div
      className={classnames(styles.content, {
        [styles.contentDropdownOpen]: shouldExpandForDropdown,
      })}
    >
      <div className={styles.headerRow}>
        <div>{t('networks.trunk.label')}</div>
        <div>{t('virtualMachines.configure.table.headings.name')}</div>
        <div>{t('virtualMachines.configure.table.headings.network')}</div>
        <div>{t('virtualMachines.configure.table.headings.ipAddress')}</div>
        <div></div>
        <div>{t('virtualMachines.configure.table.headings.assignDhcp')}</div>
        <div>{t('virtualMachines.configure.table.headings.macAddress')}</div>
        <div></div>
        <div></div>
      </div>

      {fields.map((field, index) => {
        const isRowTrunked = watchedInterfaces?.[index]?.trunked === true;

        return (
          <div key={field.id}>
            {determineShouldHideRow(index) && (
              <div className={styles.configureGrid}>
                <div>
                  <div className={styles.flex}>
                    <ControlledCheckbox
                      control={control}
                      defaultValue={Boolean(interfaces[index].trunked)}
                      label=""
                      id={`interfaces.${index}.trunked`}
                      customOnChange={onChange(index)}
                      testId={`interfaces.${index}.trunked-checkbox`}
                    />
                  </div>
                </div>
                <div>
                  <ControlledTextfield
                    control={control}
                    label=""
                    name={`interfaces.${index}.name`}
                    required={false}
                    disabled={true}
                    defaultValue={interfaces[index].name}
                  />
                </div>
                <div>
                  {isRowTrunked ? (
                    <ControlledMultipleSelect
                      control={control}
                      name={`interfaces.${index}.trunkedNetworks`}
                      label=""
                      horizontal={false}
                      options={getTrunkedNetworkOptions(networks)}
                      customOnChange={handleTrunkedNetworksChange(index)}
                      defaultValue={interfaces[index].trunkedNetworks ?? []}
                      testId={`interfaces.${index}.trunked-networks`}
                      error={
                        (
                          errors.interfaces?.[index]?.trunkedNetworks as
                            | FieldError
                            | undefined
                        )?.message
                      }
                      formGroupTextClass={styles.networkSelect}
                      onOpenChange={(isOpen) => {
                        setOpenDropdownRowIndex(isOpen ? index : undefined);
                      }}
                    />
                  ) : (
                    <ControlledSelect
                      control={control}
                      error={determineNetworkErrorToDisplay(
                        errors,
                        index,
                        getValues().interfaces?.[index].network || undefined,
                        networks,
                      )}
                      label=""
                      name={`interfaces.${index}.network`}
                      options={options}
                      defaultValue={interfaces[index].network}
                      formGroupTextClass={styles.networkSelect}
                      testId="network-select"
                    />
                  )}
                </div>
                <div>
                  {determineShouldShowAccessFields(index) && (
                    <ControlledTextfield
                      control={control}
                      error={determineIpErrorToDisplay(
                        errors,
                        index,
                        networks,
                        getValues().interfaces[index].network,
                      )}
                      label=""
                      name={`interfaces.${index}.ipAddress`}
                      required={false}
                      defaultValue={interfaces[index].ipAddress}
                      testId={`interfaces.${index}.ipAddress-input`}
                    />
                  )}
                </div>
                <div>
                  {determineShouldShowAccessFields(index) &&
                    errors.interfaces?.[index]?.ipAddress?.message ===
                      t('virtualMachines.add.duplicateIp') && (
                      <Tooltip
                        position="left"
                        size="medium"
                        text={t('virtualMachines.add.duplicateIp')}
                        id={`duplicate-warning-interfaces-${index}-ipAddress`}
                      >
                        <Icon
                          className={styles.duplicateIcon}
                          name="warning"
                          testId={`duplicate-warning-interfaces-${index}-ipAddress`}
                        ></Icon>
                      </Tooltip>
                    )}
                </div>
                <div>
                  <div className={styles.flex}>
                    {determineShouldShowAccessFields(index) && (
                      <ControlledCheckbox
                        control={control}
                        defaultValue={interfaces[index].assignDhcp}
                        label=""
                        id={`interfaces.${index}.assignDhcp`}
                      />
                    )}
                  </div>
                </div>

                <div>
                  {determineShouldShowAccessFields(index) && (
                    <ControlledTextfield
                      control={control}
                      label=""
                      name={`interfaces.${index}.macAddress`}
                      required={false}
                      error={determineMacErrorToDisplay(
                        errors,
                        index,
                        networks,
                        getValues().interfaces[index].network,
                      )}
                      defaultValue={interfaces[index].macAddress}
                      testId={`interfaces.${index}.macAddress-input`}
                    />
                  )}
                </div>
                <div>
                  {determineShouldShowAccessFields(index) &&
                    errors.interfaces?.[index]?.macAddress?.message ===
                      t('virtualMachines.add.duplicateMac') && (
                      <Tooltip
                        position="left"
                        size="medium"
                        text={t('virtualMachines.add.duplicateMac')}
                        id={`duplicate-warning-interfaces-${index}-macAddress`}
                      >
                        <Icon
                          className={styles.duplicateIcon}
                          name="warning"
                          testId={`duplicate-warning-interfaces-${index}-macAddress`}
                        ></Icon>
                      </Tooltip>
                    )}
                </div>
                <div>
                  <div className={styles.flex}>
                    {determineShowDeleteIcon(getValues().interfaces) && (
                      <IconButton
                        icon="delete"
                        colour="link"
                        onClick={() => setHiddenRows([...hiddenRows, index])}
                        testId={`vm-configure-networking-table-delete-${
                          getValues().interfaces?.[index].name
                        }`}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
